<template>
  <div class="bg-white col full-width">
    <div class="desktop-single-column q-px-md">
      <ClaimSubmitted
        :class="['q-mt-lg', { 'bdr-a-grey-4 rounded-borders': !isMobile }]"
        :img="images.claimSubmitted"
        v-if="submitted"
        :is-mobile="isMobile"
        @restart="$emit('restart')"
        @close="$emit('close')"
      />
      <div v-else :class="{ 'q-pb-lg': isMobile }">
        <BtnBack class="q-my-md" @click="$emit('close')" />
        <div
          :class="{
            'q-pa-lg rounded-borders-lg bdr-a-grey-4 q-mb-lg': !isMobile
          }"
        >
          <h4 class="text-grey-10 text-weight-medium q-mb-lg">
            Make an EV claim
          </h4>

          <div :class="isMobile ? 'column' : 'row q-col-gutter-lg'">
            <div class="col">
              <p class="text-weight-bold q-mb-sm">Paying into</p>
              <div class="q-mb-lg row">
                <div class="col" v-if="accountEndingIn">
                  Account ending in {{ accountEndingIn }}
                </div>
                <SAILink
                  class="cursor-pointer"
                  :disable="saving || submitted"
                  :label="accountEndingIn ? 'Change' : 'Add'"
                  @click="$emit('open:bank-details')"
                />
                <p class="text-body text-negative full-width q-mt-sm" v-if="bankDetailsError">{{ bankDetailsError }}</p>
              </div>
              <p class="text-weight-bold q-mb-sm">This claim is for</p>
              <div class="row q-gutter-sm scroll q-mb-lg">
                <div v-for="lease in leases" :key="lease.id">
                  <SAICardToggle
                    :error="errors.payload.leaseId.$error"
                    :disable="saving || submitted"
                    :active="leaseId === lease.id"
                    @click="$emit('select:lease', lease.id)"
                  >
                    <p
                      class="text-weight-medium q-py-sm q-px-lg text-capitalize"
                    >
                      {{ lease.vehicleRego }}
                    </p>
                  </SAICardToggle>
                </div>
              </div>
              <p class="text-weight-bold q-mb-sm">Expense Type*</p>
              <div class="row q-mb-lg">
                <BtnToggleRound
                  class="col-6 q-mb-md"
                  v-for="type in claimTypes"
                  :key="type.value"
                  :error="errors.payload.claimType.$error"
                  :active="true"
                  :disable="saving || submitted"
                  :icon="type.icon"
                  :label="type.label"
                  @click="$emit('select:claim-type', type.value)"
                />
              </div>
              <div class="q-mb-sm">
                <span class="text-paragraph text-weight-bold q-mr-xs">
                  {{ `Odometer reading from ${odometerFromDate}` }}
                </span>
              </div>
              <SAIInput
                :error="errors.payload.amountTotal.$error"
                :class="{ 'q-mb-lg': isMobile }"
                placeholder="0"
                type="number"
                :value="odometerFrom"
                :disable="saving || submitted"
                @input="$emit('update:odometerFrom', $event)"
              />
              <div class="q-mb-sm">
                <span class="text-paragraph text-weight-bold q-mr-xs">
                  {{ odometerToLabel }}
                </span>
              </div>
              <SAIInput
                :error="errors.payload.amountTotal.$error"
                :class="{ 'q-mb-lg': isMobile }"
                placeholder="0"
                type="number"
                :value="odometerTo"
                :disable="saving || submitted"
                @input="$emit('update:odometerTo', $event)"
              />
              <div class="q-mb-sm">
                <span class="text-paragraph text-weight-bold q-mr-xs">
                  Total Kilometres to claim
                </span>
              </div>
              <SAIInput
                :error="errors.payload.amountTotal.$error"
                :class="{ 'q-mb-lg': isMobile }"
                placeholder="0"
                type="number"
                :value="totalKilometers"
                :disable="true"
              />
              <div class="q-mt-lg q-mb-sm">
                <span class="text-paragraph text-weight-bold q-mr-xs">
                  Total Amount*
                </span>
                <span>(inc GST)</span>
              </div>
              <SAIInput
                :error="errors.payload.amountTotal.$error"
                :class="[ q-mb-lg, { 'q-mb-lg': isMobile } ]"
                placeholder="0"
                prefix="$"
                type="number"
                :value="amount"
                :disable="true"
              />
            </div>

            <div class="col">
              <p class="text-weight-bold q-mb-sm">Important information</p>
              <p class="q-mb-lg text-body">
                The Australian Taxation Office (ATO) allows individuals who have
                incurred electricity costs charging a battery electric vehicle
                (BEV) to claim a reimbursement. The reimbursement is calculated
                by multiplying the number of kilometres travelled by the BEV in
                the relevant Fringe Benefits Tax (FBT) year by the EV home
                charging rate for that FBT year determined by the ATO.
              </p>
              <p class="q-mb-lg text-body">
                The EV home charging rate for the {{odometerFromYear}}-{{odometerToYear}} FBT year is 4.2 cents per kilometre.
              </p>
                
              <p class="q-mb-lg text-body">
                By submitting a claim, you acknowledge that this method of
                reimbursement is in respect of all kilometres travelled in the
                year and no other claims for charging costs using the actual
                cost reimbursement method can be used.
              </p>
                 
              <p class="q-mb-lg text-body">
                For more information, see
                the ATO's <a href="https://www.ato.gov.au/law/view/document?DocID=COG/PCG20242/NAT/ATO/00001&PiT=99991231235958" target="_blank">Practical Compliance Guideline.</a> 
              </p>
              
              <p class="text-weight-bold q-mb-sm">Your Agreement</p>
              <p class="text-body">
                I understand that submission of false or misleading information
                may lead to tax offences. By submitting this claim I acknowledge
                the information is true and correct. I understand and agree to
                the <a href="https://www.fleetpartners.com.au/terms-of-use" target="_blank">Terms of Use</a> and to the handling of my personal information
                in accordance with FleetPartners <a href="https://www.fleetpartners.com.au/privacy-policy" target="_blank">Privacy Policy</a>. FleetPartners
                provides general information only. Independent financial and
                taxation advice should be sought.
              </p>
              <SAIBtn
                :class="['q-mt-lg', { 'full-width': isMobile }]"
                size="16px"
                no-caps
                padding="15px 48px"
                :loading="saving"
                @click="$emit('submit')"
              >
                <p class="text-weight-medium">Submit</p>
              </SAIBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  SAICardToggle,
  SAIInput,
  SAIFileDisplay,
  SAIBtn,
  SAILink
} from 'ecx.saigon.ui.common';

export default {
  name: 'ClaimsEvNew',
  props: {
    isMobile: Boolean,
    entity: String,
    saving: Boolean,
    submitted: Boolean,
    leases: {
      type: Array,
      default: () => []
    },
    claimTypes: {
      type: Array,
      default: () => []
    },
    leaseId: String,
    amount: Number,
    claimType: String,
    accountEndingIn: String,
    files: {
      type: Array,
      default: () => []
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    images: {
      type: Object,
      default: () => ({})
    },
    fileRequirements: {
      type: Object,
      default: () => ({})
    },
    bankDetailsError: String,
    odometerFromDate: String,
    odometerFrom: Number,
    odometerTo: Number,
    totalKilometers: Number,
  },
  computed: {
    odometerToYear() {
      const today = new Date();
      const year = today.getFullYear();
      return year;
    },
    odometerFromYear() {
      const today = new Date();
      const year = today.getFullYear();
      return year-1;
    },
    odometerToLabel() {
      const today = new Date();
      const year = today.getFullYear();
      return `Odometer reading to 31/03/${year}*`;
    },
  },
  components: {
    SAICardToggle,
    SAIInput,
    SAIBtn,
    SAILink,
    BtnBack: () => import('../../atom/btn-back'),
    ClaimSubmitted: () => import('../../molecule/claim-submitted'),
    BtnToggleRound: () => import('../../atom/btn-toggle-round')
  }
};
</script>
